import { Suspense, useEffect, useMemo, useRef } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

// Config
import routesConstants from '@constants/routeConstants';
import { WindowSizeProvider } from '@context/window/WindowSizeContext';
import {
  SinglePageSwitchLazy,
  OrderConfirmationLazy,
  MainLazy,
  Auth0CallbackGuestLazy,
  Auth0CallbackAuthenticatedLazy,
  Auth0CallbackOrderClaimLazy,
  Auth0CallbackCreateAccountLazy,
  AgeVerificationCallbackLazy,
} from '@utils/lazy/lazyMainBundle';
import { setNavigator } from '@utils/navigation/navigationService';

// Components
import App from './App';
import Login from '@views/login/Login';
import SessionManager from '@views/login-session/SessionManager';
import NotFound from '@views/not-found/NotFound';
import SessionExpired from '@views/session-expired';

const Fallback = () => <div />;

const SinglePageSwitch = () => (
  <Suspense fallback={<Fallback />}>
    <SinglePageSwitchLazy />
  </Suspense>
);

const OrderConfirmation = () => (
  <Suspense fallback={<Fallback />}>
    <OrderConfirmationLazy />
  </Suspense>
);

const Auth0CallbackGuest = () => (
  <Suspense fallback={<Fallback />}>
    <Auth0CallbackGuestLazy />
  </Suspense>
);

const Auth0CallbackAuthenticated = () => (
  <Suspense fallback={<Fallback />}>
    <Auth0CallbackAuthenticatedLazy />
  </Suspense>
);

const Auth0CallbackOrderClaim = () => (
  <Suspense fallback={<Fallback />}>
    <Auth0CallbackOrderClaimLazy />
  </Suspense>
);

const Auth0CallbackCreateAccount = () => (
  <Suspense fallback={<Fallback />}>
    <Auth0CallbackCreateAccountLazy />
  </Suspense>
);

const AgeVerificationCallback = () => (
  <Suspense fallback={<Fallback />}>
    <AgeVerificationCallbackLazy />
  </Suspense>
);

const MainManager = () => (
  <Suspense fallback={<p>Loading...</p>}>
    <MainLazy>
      <Routes>
        {/* SECURE */}
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE} />
        {/* SECURE DELIVERY */}
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_DELIVERY_ADDRESS_BOOK_EDIT} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_DELIVERY_ADDRESS_BOOK_NEW} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_DELIVERY_ADDRESS_BOOK} />
        {/* SECURE CLICK AND COLLECT */}
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_CLICK_AND_COLLECT_SEARCH_MAP} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_CLICK_AND_COLLECT_SEARCH_LIST} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_CLICK_AND_COLLECT_STORE_DETAILS} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_CLICK_AND_COLLECT_SAVED_COLLECTION_POINTS} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_CLICK_AND_COLLECT_STORE_SELECTED} />
        {/* SECURE PAYMENT */}
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_PAYMENT_ADDRESS_BOOK} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_PAYMENT_ADDRESS_BOOK_EDIT} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_PAYMENT_ADDRESS_BOOK_NEW} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_PAYMENT_GIFT_OPTIONS} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_PAYMENT_STORE_DETAILS} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_PAYMENT_CARD_DELETE} />
        <Route element={<SinglePageSwitch />} path={routesConstants.SECURE_PAYMENT_POS_CREDIT_CALLBACK} />
        {/* DELIVERY */}
        <Route element={<SinglePageSwitch />} path={routesConstants.DELIVERY_ADDRESS_BOOK_EDIT} />
        <Route element={<SinglePageSwitch />} path={routesConstants.DELIVERY_ADDRESS_BOOK_NEW} />
        <Route element={<SinglePageSwitch />} path={routesConstants.DELIVERY_ADDRESS_BOOK} />
        <Route element={<SinglePageSwitch />} path={routesConstants.DELIVERY} />
        {/* CLICK AND COLLECT */}
        <Route element={<SinglePageSwitch />} path={routesConstants.CLICK_AND_COLLECT_SAVED_COLLECTION_POINTS} />
        <Route element={<SinglePageSwitch />} path={routesConstants.CLICK_AND_COLLECT_SEARCH_LIST} />
        <Route element={<SinglePageSwitch />} path={routesConstants.CLICK_AND_COLLECT_SEARCH_MAP} />
        <Route element={<SinglePageSwitch />} path={routesConstants.CLICK_AND_COLLECT_STORE_DETAILS} />
        <Route element={<SinglePageSwitch />} path={routesConstants.CLICK_AND_COLLECT_STORE_SELECTED} />
        <Route element={<SinglePageSwitch />} path={routesConstants.CLICK_AND_COLLECT} />
        {/* PAYMENT */}
        <Route element={<SinglePageSwitch />} path={routesConstants.PAYMENT_ADDRESS_BOOK_NEW} />
        <Route element={<SinglePageSwitch />} path={routesConstants.PAYMENT_ADDRESS_BOOK_EDIT} />
        <Route element={<SinglePageSwitch />} path={routesConstants.PAYMENT_ADDRESS_BOOK} />
        <Route element={<SinglePageSwitch />} path={routesConstants.PAYMENT_CARD_DELETE} />
        <Route element={<SinglePageSwitch />} path={routesConstants.PAYMENT_GIFT_OPTIONS} />
        <Route element={<SinglePageSwitch />} path={routesConstants.PAYMENT_STORE_DETAILS} />
        <Route element={<SinglePageSwitch />} path={routesConstants.PAYMENT} />
        <Route element={<SinglePageSwitch />} path={routesConstants.PAYMENT_POS_CREDIT_CALLBACK} />
        {/* EDIT BASKET */}
        <Route element={<SinglePageSwitch />} path={routesConstants.EDIT_BASKET} />
        {/* ORDER CONFIRMATION */}
        <Route element={<OrderConfirmation />} path={routesConstants.ORDER_CONFIRMATION_JOIN_MY_JL_EDIT} />
        <Route element={<OrderConfirmation />} path={routesConstants.ORDER_CONFIRMATION_JOIN_MY_JL_NEW} />
        <Route element={<OrderConfirmation />} path={routesConstants.ORDER_CONFIRMATION_JOIN_MY_JL} />
        <Route element={<OrderConfirmation />} path={routesConstants.ORDER_CONFIRMATION_STORE_DETAILS} />
        <Route element={<OrderConfirmation />} path={routesConstants.ORDER_CONFIRMATION} />
        <Route element={<OrderConfirmation />} path={routesConstants.JOIN_MY_JL} />
        <Route element={<OrderConfirmation />} path={routesConstants.JOIN_MY_JL_EDIT} />
        <Route element={<OrderConfirmation />} path={routesConstants.JOIN_MY_JL_NEW} />
        {/* SESSION EXPIRED */}
        <Route element={<SessionExpired />} path={routesConstants.SESSION_EXPIRED} />
        <Route element={<SinglePageSwitch />} path={routesConstants.LEAVE_CHECKOUT} />
        {/* AUTH0 CALLBACKS */}
        <Route element={<Auth0CallbackAuthenticated />} path={routesConstants.CALLBACK_LOGIN_AUTHENTICATED} />
        <Route element={<Auth0CallbackGuest />} path={routesConstants.CALLBACK_LOGIN_GUEST} />
        <Route element={<Auth0CallbackOrderClaim />} path={routesConstants.CALLBACK_ORDER_CLAIM} />
        <Route element={<Auth0CallbackCreateAccount />} path={routesConstants.CALLBACK_CREATE_ACCOUNT} />
        <Route element={<AgeVerificationCallback />} path={routesConstants.CALLBACK_AGE_VERIFICATION} />
        <Route element={<AgeVerificationCallback />} path={routesConstants.CALLBACK_AGE_VERIFICATION_CANCEL} />
        {/* CATCH ALL - NotFound for unmatched main routes */}
        <Route element={<NotFound />} path="*" />
      </Routes>
    </MainLazy>
  </Suspense>
);

const LoginSessionManager = () => {
  const location = useLocation();
  return (
    <SessionManager location={location}>
      <Login />
    </SessionManager>
  );
};

export const AppRouter = () => {
  const nav = useNavigate();
  const hasInitRef = useRef<boolean>(false);

  useEffect(() => {
    if (!hasInitRef.current) {
      setNavigator(nav);
      hasInitRef.current = true;
    }
  }, [nav]);

  const routes = useMemo(
    () => (
      <Routes>
        {/* LOGIN ROUTES */}
        <Route element={<LoginSessionManager />} path={routesConstants.LOGIN} />
        <Route element={<LoginSessionManager />} path={routesConstants.APPS_GUEST_HANDOVER} />
        <Route element={<LoginSessionManager />} path={routesConstants.APPS_AUTHENTICATED_HANDOVER} />
        <Route element={<LoginSessionManager />} path={routesConstants.WEB_HANDOVER} />

        {/* MAIN ROUTES - Single MainManager handles all non-login routes */}
        <Route element={<MainManager />} path="*" />
      </Routes>
    ),
    [],
  );

  return (
    <WindowSizeProvider>
      <App>
        <Suspense>{routes}</Suspense>
      </App>
    </WindowSizeProvider>
  );
};
